import { CSSProperties, memo, useCallback, useMemo } from 'react';

import {
  KeyboardArrowDown,
  KeyboardArrowRight,
  SensorsOff,
} from '@mui/icons-material';
import {
  Box,
  Collapse,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  Stack,
  Typography,
} from '@mui/material';

import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  selectRmHovered,
  selectRmLoading,
  selectRmNameWidth,
  selectRmRealtimeOn,
  selectRouteMonitoringHeaders,
} from 'state/routeMonitoringSlice';
import {
  SavedRouteAndFeature,
  TempRoute,
  deleteCurrentSavedRoute,
  selectStatsSavedRoutes,
  useSavedRouteDeleteMutation,
} from 'state/savedRouteSlices';
import {
  selectProject,
  selectRouteSelection,
  setRouteSelection,
} from 'state/workflowSlice';

import { TravelTimeChart } from 'features/chart/TravelTimeChart';
import { ChartVariant } from 'features/chart/chartOptions';
import FontAwesomeIcon from 'features/common/FontAwesomeIcon';
import {
  DragHandle,
  SortableItem,
} from 'features/common/Sortable/SortableItem';
import { useScrollIntoViewIfNeeded } from 'features/common/hooks/useScrollIntoViewIfNeeded';
import { classNames, formatTraveltime } from 'features/common/utils';
import LoadingDotAnimation from 'features/spinner/LoadingDotAnimation';
import { primary_text_color } from 'theme/cemTheme';

import { ConditionChip } from './ConditionChip';
import RouteMenu, { TempRouteMenu } from './RouteMenu';
import { RouteSignature } from './RouteSignature';

import styles from './RouteRow.module.css';

const timelinePolling = 15 * 60 * 1000; // 15 min

const sxLoading: CSSProperties = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -70%)',
};
const chartOptions = {
  title: {
    text: 'Travel Time',
    align: 'center',
    style: {
      fontFamily: 'var(--font-display)',
      fontSize: '14px',
      fontWeight: 500,
      color: primary_text_color,
    },
  },
  yAxis: {
    title: {
      text: null,
    },
  },
  xAxis: {
    labels: {
      rotation: 0,
      style: {
        fontWeight: 400,
        color: primary_text_color,
        fontSize: '0.75rem',
      },
    },
    title: {
      text: 'Time of day',
      margin: 5,
    },
  },
  chart: {
    marginRight: '50',
    height: '239',
    backgroundColor: '#2A2824',
    plotBackgroundColor: '#2A2824',
  },
  legend: {
    align: 'center',
    verticalAlign: 'bottom',
    layout: 'horizontal',
    itemWidth: 140,
    margin: 10,
    padding: 0,
    itemDistance: 20,
    symbolWidth: 16,
    itemStyle: {
      color: primary_text_color,
      fontSize: '0.75rem',
      fontWeight: 'normal',
      whiteSpace: 'nowrap',
      textOverflow: 'none',
    },
    itemHoverStyle: {
      color: primary_text_color,
      fontSize: '0.75rem',
      fontWeight: 'bold',
    },
  },
};

function isSavedRouteAndFeature(
  route: TempRoute | SavedRouteAndFeature,
): route is SavedRouteAndFeature {
  return 'savedRoute' in route;
}

interface RouteRowProps {
  route: SavedRouteAndFeature | TempRoute;
  isOpen: boolean;
  onSelect: (savedRouteId: string) => void;
  onHover?: (routeId: string | null) => void;
  disableOrdering: boolean;
}

const ComparedToTypical = memo(
  ({
    current,
    typical,
    routeId,
  }: {
    current: number;
    typical: number;
    routeId: string;
  }) => {
    const diff = Math.trunc(current) - Math.trunc(typical);
    const isHigher = diff > 0;

    return (
      <Stack
        direction="row"
        gap={0.5}
        alignItems="center"
        key={`${routeId}_${isHigher}`}
      >
        <FontAwesomeIcon
          size="0.7rem"
          name={isHigher ? 'circle-arrow-up' : 'circle-arrow-down'}
          classes={isHigher ? [styles.arrow_up] : [styles.arrow_down]}
          sx={{ padding: 0 }}
        />
        <Typography
          variant="caption"
          color="rgba(245, 245, 245, 0.70)" // ideally move to theme
        >
          {formatTraveltime(Math.abs(diff))}
        </Typography>
      </Stack>
    );
  },
);

export function RouteRow({
  route,
  isOpen,
  onSelect,
  disableOrdering,
  onHover,
}: RouteRowProps) {
  const dispatch = useAppDispatch();
  const project_slug = useAppSelector(selectProject);
  const stats = useAppSelector(selectStatsSavedRoutes);
  const routeSelection = useAppSelector(selectRouteSelection);
  const headers = useAppSelector(selectRouteMonitoringHeaders);
  const rmLoading = useAppSelector(selectRmLoading);
  const realtime = useAppSelector(selectRmRealtimeOn);
  const hoveredRouteId = useAppSelector(selectRmHovered);
  const isTemp = !isSavedRouteAndFeature(route);
  const loading = rmLoading || (isTemp && stats?.[route.id] === undefined);
  const nameWidth = useAppSelector(selectRmNameWidth);

  const [deleteSavedRoute] = useSavedRouteDeleteMutation();

  const getRouteName = useCallback(() => {
    return isTemp ? route.routeName : route.savedRoute.routeName;
  }, [isTemp, route]);

  const conditionIcons = useMemo<Record<number, JSX.Element>>(
    () => ({
      0: <ConditionChip variant="freeFlow" />,
      1: <ConditionChip variant="moderate" />,
      2: <ConditionChip variant="severe" />,
    }),
    [],
  );

  const { cardinal_direction, route_length } = useMemo(() => {
    if (isTemp) {
      return {
        cardinal_direction: route.cardinal_direction,
        route_length: route.route_length,
      };
    } else {
      return {
        cardinal_direction: route.feature.properties.cardinal_direction,
        route_length: route.feature.properties.route_length,
      };
    }
  }, [isTemp, route]);

  const handleDelete = useCallback(
    (id: string) => {
      deleteSavedRoute({
        project_slug: project_slug as string,
        savedRouteId: id,
      });
      dispatch(deleteCurrentSavedRoute(id));
      if (routeSelection === id) dispatch(setRouteSelection(undefined));
    },
    [project_slug, routeSelection],
  );

  const handleSelect = () => {
    onSelect(route.id);
  };
  const [routeRowRef, scrollIntoViewIfNeeded] =
    useScrollIntoViewIfNeeded<HTMLDivElement>();

  return (
    <Stack
      ref={routeRowRef}
      id={isOpen ? styles.route_row_expanded : undefined}
      className={classNames(
        styles.route_row,
        hoveredRouteId === route.id && styles.hovered,
      )}
    >
      <SortableItem
        onMouseEnter={() => onHover?.(route.id)}
        onMouseLeave={() => onHover?.(null)}
        id={route.id}
        style={{
          display: 'flex',
          zIndex: 200,
          flexDirection: 'row',
        }}
      >
        <DragHandle
          isDisabled={hoveredRouteId !== route.id || disableOrdering}
        />
        <ListItem
          disableGutters
          className={styles.route_list_item}
          secondaryAction={
            !loading &&
            (!isTemp ? (
              <RouteMenu
                hasAlertEvent={stats?.[route.id]?.hasAlertEvent ?? false}
                route={route}
                deleteRoute={handleDelete}
              />
            ) : (
              <TempRouteMenu route={route} />
            ))
          }
          onClick={handleSelect}
        >
          <Grid container spacing={2}>
            <Grid item className={styles.grid_item} sx={{ width: nameWidth }}>
              <Box
                display="flex"
                alignItems="center"
                gap={0.25}
                className={styles.hidden_overflow}
              >
                <IconButton size="small" sx={{ alignSelf: 'self-start' }}>
                  {isOpen ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                </IconButton>
                <Box
                  title={getRouteName()}
                  display="flex"
                  flexDirection="column"
                  className={styles.hidden_overflow}
                >
                  <Typography variant="body2" id={styles.route_name}>
                    {getRouteName()}
                  </Typography>
                  <RouteSignature
                    cardinal_direction={cardinal_direction}
                    route_length={route_length}
                  />
                </Box>
              </Box>
            </Grid>
            {headers.slice(1).map((el) => (
              <Grid
                key={`${route.id}_${el.title}}`}
                item
                className={styles.grid_item}
                sx={{
                  visibility: loading ? 'hidden' : 'visible',
                  width: `${el.size}px`,
                }}
              >
                {el.val === 'conditionScore' ? (
                  (conditionIcons[stats?.[route.id]?.[el.val]] ?? (
                    <SensorsOff />
                  ))
                ) : (
                  <Box
                    display="flex"
                    flexDirection="column"
                    className={styles.show_overflow} // some time values are overflowing the cell width
                  >
                    {stats?.[route.id]?.[el.val] != null ? (
                      <>
                        <Typography variant="body2">
                          {formatTraveltime(
                            Math.trunc(stats?.[route.id]?.[el.val]),
                          )}
                        </Typography>
                        {el.typical &&
                          stats?.[route.id]?.[el.typical] != null && (
                            <ComparedToTypical
                              current={stats[route.id][el.val]}
                              typical={stats[route.id][el.typical]}
                              routeId={route.id}
                            />
                          )}
                      </>
                    ) : (
                      <SensorsOff />
                    )}
                  </Box>
                )}
              </Grid>
            ))}

            {loading && <LoadingDotAnimation sx={sxLoading} />}
          </Grid>
        </ListItem>
      </SortableItem>
      <Divider className={classNames(isOpen && styles.row_divider_expanded)} />
      <Collapse
        in={isOpen}
        timeout="auto"
        unmountOnExit
        onEntered={scrollIntoViewIfNeeded}
      >
        <List component="div" disablePadding>
          <TravelTimeChart
            pollingInterval={timelinePolling}
            useDateRange={!realtime}
            variant={ChartVariant.RouteMonitoring}
            additionalChartOptions={chartOptions}
          />
        </List>
      </Collapse>
    </Stack>
  );
}
